// Authorization gate between MSAL login (AuthProvider) and the chat shell.
//
// A valid Entra token is NOT sufficient to use the chat UI — the user must be a
// chat_user / admin in at least one tenant. The backend already 403s every
// tenant-scoped route for a non-member (GET /v1/me/tenants returns 200 [] — a
// deliberately ambiguous "you have no tenants" signal). Pre-gate, AppShell
// silently no-op'd on the empty list and rendered the full shell anyway, so a
// Bekaert user with no membership saw the entire UI. This wrapper closes that
// gap: it resolves the caller's tenants ONCE and renders the shell only when
// the list is non-empty.
//
//   loading   — fetch in flight (or MSAL accounts not yet resolved)
//   ready     — ≥1 tenant → set the active tenant + render children (the shell)
//   no_access — empty list → render the no-access screen (also covers reader-
//               only users, who are excluded from /v1/me/tenants)
//   error     — fetch threw → render the reload screen. A TRANSIENT failure
//               (network / token) must NOT be treated as "no access" or it
//               would lock out legitimately-entitled users on a blip.

import { type ReactNode, useEffect, useState } from "react";
import { tenantsApi } from "@/api/tenants";
import { pca } from "@/auth/AuthProvider";
import { AuthScreen } from "@/auth/AuthScreen";
import { useAccessToken } from "@/auth/useAccessToken";
import { useChatStore } from "@/runtime/chatStore";
import { NoAccessScreen } from "./NoAccessScreen";

type GatePhase = "loading" | "ready" | "no_access" | "error";

export function TenantGate({ children }: { children: ReactNode }) {
  const activeTenantId = useChatStore((s) => s.activeTenantId);
  const setActiveTenant = useChatStore((s) => s.setActiveTenant);
  const { acquireToken, accounts } = useAccessToken();
  const [phase, setPhase] = useState<GatePhase>(() => (activeTenantId ? "ready" : "loading"));

  useEffect(() => {
    // Already resolved (in-session switch, or a prior gate pass) → trust it.
    if (activeTenantId) {
      setPhase("ready");
      return;
    }
    // AuthProvider mounts children only after login, so accounts should be ≥1;
    // guard defensively and keep showing the loading screen until it is.
    if (accounts.length === 0) return;

    let cancelled = false;
    setPhase("loading");
    (async () => {
      try {
        const token = await acquireToken();
        const tenants = await tenantsApi.myTenants(token);
        if (cancelled) return;
        if (tenants.length > 0) {
          setActiveTenant(tenants[0]);
          setPhase("ready");
        } else {
          setPhase("no_access");
        }
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn("[chat-ui] failed to load tenants", err);
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTenantId, accounts.length, acquireToken, setActiveTenant]);

  if (phase === "no_access") {
    return <NoAccessScreen onSignOut={() => void pca.logoutRedirect()} accountName={accounts[0]?.username} />;
  }
  if (phase === "error") {
    return <AuthScreen variant="error" message="We couldn't load your workspaces." />;
  }
  if (phase !== "ready") {
    return <AuthScreen variant="loading" />;
  }
  return <>{children}</>;
}
